import axios, { AxiosInstance, AxiosRequestHeaders } from "axios";
import config from "../../config";

const API_BASE = config.API_URL
  ? `${config.API_URL.replace(/\/$/, "")}/api`
  : "/api";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Sécurité définitive : en dev (localhost), ne jamais appeler la prod → évite CORS
const isDev = process.env.NODE_ENV === "development";
const isProductionDomain = (url: string) =>
  typeof url === "string" && /https?:\/\/(?!localhost)[^/]*admin-mogadelivery\.com/.test(url);
axiosInstance.interceptors.request.use((reqConfig) => {
  const base = reqConfig.baseURL || "";
  if (isDev && isProductionDomain(base)) {
    console.warn("[API] Dev: redirection des appels prod vers le proxy (/api). Lancez le backend sur le port 5001.");
    reqConfig.baseURL = "/api";
  }
  return reqConfig;
});

const AUTH_SESSION_KEY = "moga_user";

export const getStoredToken = (): string | null => {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return (
      data?.accessToken ??
      data?.token ??
      data?.access_token ??
      null
    );
  } catch {
    return null;
  }
};

/** Refresh token (for cross-origin refresh when cookie is not sent) */
export const getStoredRefreshToken = (): string | null => {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data?.refreshToken ?? data?.refresh_token ?? null;
  } catch {
    return null;
  }
};

const persistTokens = (accessToken?: string, refreshToken?: string) => {
  if (!accessToken) return;
  const raw = localStorage.getItem(AUTH_SESSION_KEY);
  if (!raw) return;
  try {
    const user = JSON.parse(raw);
    localStorage.setItem(
      AUTH_SESSION_KEY,
      JSON.stringify({
        ...user,
        accessToken,
        ...(refreshToken ? { refreshToken } : {}),
      })
    );
  } catch (_) {}
};

/** POST /sessions/refresh with the stored token (body + header). Cookie alone is not enough on localhost. */
export const postSessionRefresh = () => {
  const refreshToken = getStoredRefreshToken();
  return axiosInstance.post(
    "sessions/refresh",
    refreshToken ? { refreshToken } : {},
    {
      withCredentials: true,
      timeout: 8000,
      headers: refreshToken ? { "X-Refresh-Token": refreshToken } : undefined,
    }
  );
};

// Attach accessToken to every request (Bearer is standard and rarely stripped by proxies/CORS)
axiosInstance.interceptors.request.use((reqConfig) => {
  const token = getStoredToken();
  if (token) {
    if (!reqConfig.headers) reqConfig.headers = {} as AxiosRequestHeaders;
    (reqConfig.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    (reqConfig.headers as Record<string, string>).authorizationtoken = token;
  }
  if (process.env.NODE_ENV === "development") {
    try {
      const base = (reqConfig.baseURL ?? "").toString().replace(/\/$/, "");
      const path = (reqConfig.url ?? "").toString().replace(/^\//, "");
      const url = base && path && !path.startsWith("http") ? `${base}/${path}` : reqConfig.url ?? path;
      console.log("[API]", reqConfig.method?.toUpperCase(), url || path);
    } catch (_) {}
  }
  return reqConfig;
});

if (process.env.NODE_ENV === "development") {
  // Logging already done in the interceptor above
}

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) =>
    error ? prom.reject(error) : prom.resolve(token)
  );
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => {
    const url = String(response?.config?.url || "").replace(/^\//, "");
    if (url.startsWith("sessions/refresh") && response?.data?.accessToken) {
      persistTokens(response.data.accessToken, response.data.refreshToken);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;

    // Un 401 sur une requête depuis une page /auth/* (login, register, forget-
     // password, resend-email…) NE DOIT PAS déclencher un window.location.href
     // vers /auth/login : ça reload la page et efface le message d'erreur
     // Redux avant que l'utilisateur ait pu le lire. Sur les auth endpoints
     // (sessions, users/register, users/forget-password…), on laisse le 401
     // remonter au composant qui affichera un message inline.
     const requestUrl = String(originalRequest?.url || "").replace(/^\//, "");
     const isAuthEndpoint =
       requestUrl.startsWith("sessions") ||
       requestUrl.startsWith("users/register") ||
       requestUrl.startsWith("users/forget-password") ||
       requestUrl.startsWith("users/reset-password") ||
       requestUrl.startsWith("users/resend-email");
     const isOnAuthPage =
       typeof window !== "undefined" &&
       window.location.pathname.startsWith("/auth/");
     const shouldSkipAuthRedirect = isAuthEndpoint || isOnAuthPage;

    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Sur un endpoint d'auth (login, signup, forget-password…), un 401
      // n'est pas une session expirée : c'est simplement des identifiants
      // invalides. Il ne faut ni tenter de refresh (inutile), ni rediriger.
      // On laisse remonter tel quel pour que le composant affiche l'erreur.
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      // No token in session (e.g. old session before we sent accessToken) -> redirect to login, no refresh
      if (!getStoredToken()) {
        localStorage.removeItem(AUTH_SESSION_KEY);
        if (typeof window !== "undefined" && !shouldSkipAuthRedirect) {
          window.location.href = "/auth/login";
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axiosInstance(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return postSessionRefresh()
        .then((refreshRes) => {
          persistTokens(refreshRes?.data?.accessToken, refreshRes?.data?.refreshToken);
          processQueue(null, null);
          return axiosInstance(originalRequest);
        })
        .catch((err) => {
          processQueue(err, null);
          // Distinguer un vrai refus d'authentification (session invalide/
          // révoquée) d'une erreur transitoire (offline, backend indispo).
          // Le premier cas → logout + redirect. Le second → on garde la
          // session pour ne pas déloguer un user juste parce qu'il n'a pas
          // de réseau à l'ouverture de l'app.
          const status = err?.response?.status;
          const isAuthFailure = status === 401 || status === 403;
          if (isAuthFailure) {
            localStorage.removeItem("moga_user");
            if (typeof window !== "undefined" && !shouldSkipAuthRedirect) {
              window.location.href = "/auth/login";
            }
          } else if (process.env.NODE_ENV === "development") {
            console.warn(
              "[apiCore] refresh transient failure (offline/network) — session kept",
              err?.code || err?.message
            );
          }
          return Promise.reject(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    }

    // Resolve instead of reject for 403 so no unhandledrejection (CRA overlay). Sagas check response.status === 403.
    if (error?.response?.status === 403) {
      if (process.env.NODE_ENV === "development" && error?.config) {
        try {
          const base = (error.config.baseURL ?? "").toString().replace(/\/$/, "");
          const path = (error.config.url ?? "").toString().replace(/^\//, "");
          const url = base && path && !path.startsWith("http") ? `${base}/${path}` : error.config.url ?? "";
          console.warn("[API] 403 Forbidden:", url || path);
        } catch (_) {}
      }
      return Promise.resolve({
        data: null,
        status: 403,
        config: error?.config,
        __handled403: true,
      });
    }

    // ─── Auto-retry sur pannes réseau transitoires ──────────────────────
    // Cible : timeout axios, coupure réseau, 502/503/504 côté nginx/backend
    // (typiquement pendant un déploiement CI qui redémarre pm2). Retry limité
    // aux méthodes idempotentes (GET, HEAD) pour ne PAS risquer une double
    // écriture sur un POST/PUT/DELETE dont la réponse s'est perdue.
    const method = (originalRequest?.method || "get").toLowerCase();
    const isIdempotent = method === "get" || method === "head";
    const status = error?.response?.status;
    const isRetryable =
      isIdempotent &&
      (error?.code === "ECONNABORTED" || // timeout axios
        error?.code === "ERR_NETWORK" ||  // coupure DNS / offline
        status === 502 ||
        status === 503 ||
        status === 504);
    const MAX_RETRIES = 2;
    if (isRetryable && originalRequest) {
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      if (originalRequest._retryCount <= MAX_RETRIES) {
        const delay = 500 * originalRequest._retryCount; // 500ms puis 1000ms
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `[API Retry ${originalRequest._retryCount}/${MAX_RETRIES}] ${method.toUpperCase()} ${originalRequest.url} — ${error?.code || status}`
          );
        }
        await new Promise((r) => setTimeout(r, delay));
        return axiosInstance(originalRequest);
      }
    }

    if (process.env.NODE_ENV === "development" && error?.config) {
      try {
        const base = (error.config.baseURL ?? "").toString().replace(/\/$/, "");
        const path = (error.config.url ?? "").toString().replace(/^\//, "");
        const url = base && path && !path.startsWith("http") ? `${base}/${path}` : error.config.url ?? "";
        console.error(
          "[API Error]",
          error.config.method?.toUpperCase(),
          url || path,
          error.response?.status,
          error.message
        );
      } catch (logErr) {
        console.error("[API Error]", error?.message ?? error);
      }
    }
    return Promise.reject(error);
  }
);

/** Get the shared axios instance - use for BaseApi and auth */
export function getApiInstance(): AxiosInstance {
  return axiosInstance;
}

const getUserFromSession = () => {
  const user = localStorage.getItem(AUTH_SESSION_KEY);
  return user ? JSON.parse(user) : null;
};

// Small in-memory TTL cache for idempotent GETs. Shared across the class so
// two components that mount within `ttlMs` and hit the same URL+params only
// pay one round-trip. Not persisted — cleared on hard reload. Data-changing
// requests (create/update/delete) must call APICore.invalidateCache(url) to
// bust matching cached responses.
type CachedEntry = { data: any; ts: number; ttl: number };
const responseCache = new Map<string, CachedEntry>();
const cacheKey = (url: string, params?: any) =>
  `${url}::${params ? JSON.stringify(params) : ""}`;

class APICore {
  get = (url: string, params?: any) => {
    const queryString = params
      ? Object.keys(params)
          .map(
            (key) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
          )
          .join("&")
      : "";
    return axiosInstance.get(`${url}${queryString ? `?${queryString}` : ""}`);
  };

  /**
   * GET with in-memory TTL cache. Returns the cached response if a matching
   * URL+params was fetched less than `ttlMs` ago (default 30s), else fires
   * the request and stores the response. Same signature as get() plus the
   * ttl. `force: true` bypasses the cache (used by manual "Refresh" buttons).
   */
  getCached = (
    url: string,
    params?: any,
    ttlMs: number = 30_000,
    force: boolean = false
  ) => {
    const key = cacheKey(url, params);
    const now = Date.now();
    if (!force) {
      const hit = responseCache.get(key);
      if (hit && now - hit.ts < hit.ttl) {
        return Promise.resolve({ data: hit.data, status: 200, cached: true });
      }
    }
    return this.get(url, params).then((res: any) => {
      if (res?.__handled403 || res?.status === 403 || res?.data == null) {
        return res;
      }
      responseCache.set(key, { data: res.data, ts: now, ttl: ttlMs });
      return res;
    });
  };

  /**
   * Drop cached entries whose key starts with the given URL prefix. Call
   * from mutation code paths (POST /orders → invalidateCache('orders')) so
   * the next list load reflects the change instead of the stale cache.
   */
  static invalidateCache = (urlPrefix: string) => {
    Array.from(responseCache.keys()).forEach((k) => {
      if (k.startsWith(`${urlPrefix}::`) || k.startsWith(urlPrefix)) {
        responseCache.delete(k);
      }
    });
  };

  getFile = (url: string, params?: any) => {
    const queryString = params
      ? Object.keys(params)
          .map(
            (key) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
          )
          .join("&")
      : "";
    return axiosInstance.get(
      `${url}${queryString ? `?${queryString}` : ""}`,
      { responseType: "blob" }
    );
  };

  getMultiple = (urls: string[], params?: any) => {
    const queryString = params
      ? Object.keys(params)
          .map(
            (key) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
          )
          .join("&")
      : "";
    const requests = urls.map((url) =>
      axiosInstance.get(`${url}${queryString ? `?${queryString}` : ""}`)
    );
    return Promise.all(requests);
  };

  create = (url: string, data: any) => axiosInstance.post(url, data);
  updatePatch = (url: string, data: any) => axiosInstance.patch(url, data);
  update = (url: string, data: any) => axiosInstance.put(url, data);
  delete = (url: string) => axiosInstance.delete(url);

  createWithFile = (url: string, data: any) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => formData.append(key, data[key]));
    return axiosInstance.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  updateWithFile = (url: string, data: any) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => formData.append(key, data[key]));
    return axiosInstance.patch(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  isUserAuthenticated = () => !!this.getLoggedInUser();

  /** True only if session has a user and a token (so API calls will be authenticated) */
  hasValidAuthSession = () => !!this.getLoggedInUser() && !!getStoredToken();

  setLoggedInUser = (session: any) => {
    if (session) {
      localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(AUTH_SESSION_KEY);
    }
  };

  getLoggedInUser = () => getUserFromSession();

  setUserInSession = (modifiedUser: any) => {
    const userInfo = localStorage.getItem(AUTH_SESSION_KEY);
    if (userInfo) {
      const user = JSON.parse(userInfo);
      this.setLoggedInUser({ ...user, ...modifiedUser });
    }
  };
}

export { APICore };
